/**
 * Portal Cache Service
 * 
 * Cachar portaldata i sessionStorage för snabbare laddningstider.
 * Data uppdateras i bakgrunden efter initial laddning (stale-while-revalidate pattern).
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  slug: string;
}

// Cache TTL i millisekunder (5 minuter)
const CACHE_TTL = 5 * 60 * 1000;

// Cache keys
const CACHE_KEYS = {
  ORGANIZATION: 'portal_cache_org_',
  SETTINGS: 'portal_cache_settings_',
  CONTENT: 'portal_cache_content_',
  FULL_DATA: 'portal_cache_full_'
};

/**
 * Hämta cachad data
 */
export function getCachedData<T>(key: string, slug: string): T | null {
  try {
    const cacheKey = key + slug;
    const cached = sessionStorage.getItem(cacheKey);
    
    if (!cached) return null;
    
    const entry: CacheEntry<T> = JSON.parse(cached);
    const age = Date.now() - entry.timestamp;
    
    // Returnera data även om den är gammal (stale) - vi uppdaterar i bakgrunden
    if (entry.slug === slug) {
      return entry.data;
    }
    
    return null;
  } catch (e) {
    console.warn('Cache read error:', e);
    return null;
  }
}

/**
 * Kolla om cachad data är färsk (inom TTL)
 */
export function isCacheFresh(key: string, slug: string): boolean {
  try {
    const cacheKey = key + slug;
    const cached = sessionStorage.getItem(cacheKey);
    
    if (!cached) return false;
    
    const entry = JSON.parse(cached);
    const age = Date.now() - entry.timestamp;
    
    return age < CACHE_TTL;
  } catch {
    return false;
  }
}

/**
 * Spara data i cache
 */
export function setCachedData<T>(key: string, slug: string, data: T): void {
  try {
    const cacheKey = key + slug;
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      slug
    };
    
    sessionStorage.setItem(cacheKey, JSON.stringify(entry));
  } catch (e) {
    // sessionStorage kan vara full - rensa gammal cache
    console.warn('Cache write error, clearing old entries:', e);
    clearOldCache();
  }
}

/**
 * Invalidera cache för en specifik slug
 */
export function invalidateCache(slug: string): void {
  try {
    Object.values(CACHE_KEYS).forEach(key => {
      sessionStorage.removeItem(key + slug);
    });
  } catch (e) {
    console.warn('Cache invalidation error:', e);
  }
}

/**
 * Rensa all portal-cache
 */
export function clearAllCache(): void {
  try {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('portal_cache_')) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch (e) {
    console.warn('Cache clear error:', e);
  }
}

/**
 * Rensa gammal cache (äldre än 1 timme)
 */
function clearOldCache(): void {
  try {
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('portal_cache_')) {
        try {
          const entry = JSON.parse(sessionStorage.getItem(key) || '{}');
          if (entry.timestamp && entry.timestamp < oneHourAgo) {
            keysToRemove.push(key);
          }
        } catch {
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch (e) {
    console.warn('Old cache clear error:', e);
  }
}

// Exportera cache-nycklar för användning i komponenter
export const PORTAL_CACHE_KEYS = CACHE_KEYS;
